import { useState, useCallback, useEffect } from "react";
import { useFileUpload } from "../hooks/useFileUpload.ts";
import { FinalizeUploadResponse, formatFileSize } from "@dzajdrop/shared";

const API_URL = import.meta.env.VITE_API_URL || "/api";

function UploadPage() {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploadResult, setUploadResult] =
    useState<FinalizeUploadResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Upload options
  const [customLinkId, setCustomLinkId] = useState("");
  const [linkIdStatus, setLinkIdStatus] = useState<{
    checking: boolean;
    available: boolean | null;
    message: string;
  }>({ checking: false, available: null, message: "" });
  const [expirationDays, setExpirationDays] = useState(30);
  const [password, setPassword] = useState("");
  const [usePassword, setUsePassword] = useState(false);

  const { uploadFile, isUploading, progress } = useFileUpload({
    apiUrl: API_URL,
    onProgress: (progress) => {
      console.log("Upload progress:", progress);
    },
    onSuccess: (result) => {
      setUploadResult(result);
      setError(null);
      setSelectedFile(null);
    },
    onError: (error) => {
      setError(error);
      setUploadResult(null);
    },
  });

  // Check linkId availability
  const checkLinkId = async (linkId: string) => {
    if (!linkId.trim()) {
      setLinkIdStatus({ checking: false, available: null, message: "" });
      return;
    }

    setLinkIdStatus({
      checking: true,
      available: null,
      message: "Checking...",
    });

    try {
      const response = await fetch(
        `${API_URL}/api/check-linkid?linkId=${encodeURIComponent(linkId)}`
      );
      const data = await response.json();

      if (response.ok) {
        setLinkIdStatus({
          checking: false,
          available: data.available,
          message: data.available ? "✅ Available" : "❌ Already exists",
        });
      } else {
        setLinkIdStatus({
          checking: false,
          available: null,
          message: "Error checking availability",
        });
      }
    } catch (error) {
      setLinkIdStatus({
        checking: false,
        available: null,
        message: "Error checking availability",
      });
    }
  };

  // Debounced linkId checking
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      checkLinkId(customLinkId);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [customLinkId]);

  const handleFileSelect = useCallback((files: FileList | null) => {
    if (files && files.length > 0) {
      setSelectedFile(files[0]);
      setError(null);
      setUploadResult(null);
    }
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);
      handleFileSelect(e.dataTransfer.files);
    },
    [handleFileSelect]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setIsDragOver(false);
  }, []);

  const handleUpload = useCallback(async () => {
    if (!selectedFile) return;

    // Check if custom linkId is available
    if (customLinkId.trim() && linkIdStatus.available === false) {
      setError(
        "Custom Link ID is already taken. Please choose a different one."
      );
      return;
    }

    const linkOptions = {
      customLinkId: customLinkId.trim() || undefined,
      expirationDays,
      password: usePassword ? password : undefined,
    };

    await uploadFile(selectedFile, linkOptions);
  }, [
    selectedFile,
    customLinkId,
    linkIdStatus.available,
    expirationDays,
    password,
    usePassword,
    uploadFile,
  ]);

  const copyToClipboard = useCallback((text: string) => {
    navigator.clipboard.writeText(text);
  }, []);

  const handleClick = useCallback(() => {
    if (!selectedFile) {
      document.getElementById("file-input")?.click();
    }
  }, [selectedFile]);

  return (
    <div style={{ maxWidth: "600px", margin: "0 auto", padding: "20px" }}>
      {error && <div className="error">{error}</div>}

      {uploadResult && (
        <div className="link-result">
          <h3>Upload Complete!</h3>
          <p>Your file is ready for download:</p>
          <a href={uploadResult.url} target="_blank" rel="noopener noreferrer">
            {uploadResult.url}
          </a>
          <button
            className="upload-button"
            onClick={() => copyToClipboard(uploadResult.url)}
            style={{ marginLeft: "10px", padding: "5px 10px" }}
          >
            Copy Link
          </button>
        </div>
      )}

      {!isUploading && (
        <>
          <div
            className={`modern-upload-area ${isDragOver ? "dragover" : ""}`}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={handleClick}
            style={{
              border: `2px dashed ${isDragOver ? "#4285F4" : "#e1e5e9"}`,
              borderRadius: "16px",
              padding: "60px 40px",
              textAlign: "center",
              backgroundColor: isDragOver ? "#f8f9ff" : "#ffffff",
              cursor: selectedFile ? "default" : "pointer",
              transition: "all 0.3s ease",
              marginBottom: "20px",
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
            }}
          >
            {selectedFile ? (
              <div style={{ color: "#5f6368" }}>
                <div
                  style={{
                    fontSize: "18px",
                    fontWeight: "500",
                    marginBottom: "8px",
                  }}
                >
                  {selectedFile.name}
                </div>
                <div style={{ fontSize: "14px", color: "#9aa0a6" }}>
                  {formatFileSize(selectedFile.size)} •{" "}
                  {selectedFile.type || "Unknown type"}
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedFile(null);
                  }}
                  style={{
                    marginTop: "16px",
                    padding: "8px 16px",
                    background: "#f8f9fa",
                    border: "1px solid #dadce0",
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontSize: "14px",
                    color: "#5f6368",
                  }}
                >
                  Choose different file
                </button>
              </div>
            ) : (
              <div>
                <img
                  src="/download.svg"
                  alt="Upload"
                  width="80"
                  height="80"
                  style={{
                    filter: "drop-shadow(0 2px 4px rgba(66, 133, 244, 0.2))",
                  }}
                />
                <div
                  style={{
                    fontSize: "16px",
                    color: "#5f6368",
                    margin: "20px 0 8px 0",
                    fontWeight: "600",
                  }}
                >
                  Drop your file here, or{" "}
                  <span style={{ color: "#4285F4", fontWeight: "500" }}>
                    browse
                  </span>
                </div>
                <input
                  type="file"
                  id="file-input"
                  onChange={(e) => handleFileSelect(e.target.files)}
                  style={{ display: "none" }}
                />
              </div>
            )}
          </div>

          {selectedFile && (
            <div
              style={{
                background: "white",
                padding: "24px",
                borderRadius: "12px",
                marginBottom: "20px",
                border: "1px solid #e1e5e9",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
              }}
            >
              <h3
                style={{
                  margin: "0 0 20px 0",
                  fontSize: "18px",
                  color: "#202124",
                }}
              >
                Upload Options
              </h3>

              <div style={{ marginBottom: "20px" }}>
                <label
                  htmlFor="custom-link"
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontSize: "14px",
                    fontWeight: "500",
                    color: "#5f6368",
                  }}
                >
                  Custom Link ID (optional)
                </label>
                <input
                  type="text"
                  id="custom-link"
                  value={customLinkId}
                  onChange={(e) => setCustomLinkId(e.target.value)}
                  placeholder="e.g., my-file-2024"
                  style={{
                    width: "100%",
                    padding: "12px",
                    border: `1px solid ${
                      linkIdStatus.available === false
                        ? "#dc3545"
                        : linkIdStatus.available === true
                        ? "#34a853"
                        : "#dadce0"
                    }`,
                    borderRadius: "8px",
                    fontSize: "14px",
                    outline: "none",
                    transition: "border-color 0.2s ease",
                    boxSizing: "border-box",
                  }}
                />
                {customLinkId && (
                  <div
                    style={{
                      fontSize: "12px",
                      marginTop: "6px",
                      color:
                        linkIdStatus.available === false
                          ? "#dc3545"
                          : linkIdStatus.available === true
                          ? "#34a853"
                          : "#5f6368",
                    }}
                  >
                    {linkIdStatus.message}
                  </div>
                )}
              </div>

              <div style={{ marginBottom: "20px" }}>
                <label
                  htmlFor="expiration"
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontSize: "14px",
                    fontWeight: "500",
                    color: "#5f6368",
                  }}
                >
                  Expiration (days)
                </label>
                <input
                  type="number"
                  id="expiration"
                  value={expirationDays}
                  onChange={(e) =>
                    setExpirationDays(parseInt(e.target.value) || 30)
                  }
                  min="1"
                  max="365"
                  style={{
                    width: "100%",
                    padding: "12px",
                    border: "1px solid #dadce0",
                    borderRadius: "8px",
                    fontSize: "14px",
                    outline: "none",
                    boxSizing: "border-box",
                  }}
                />
              </div>

              <div style={{ marginBottom: "20px" }}>
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                    fontSize: "14px",
                    fontWeight: "500",
                    color: "#5f6368",
                  }}
                >
                  <input
                    type="checkbox"
                    id="use-password"
                    checked={usePassword}
                    onChange={(e) => setUsePassword(e.target.checked)}
                    style={{
                      marginRight: "8px",
                      width: "16px",
                      height: "16px",
                      accentColor: "#4285f4",
                    }}
                  />
                  Protect with password
                </label>
              </div>

              {usePassword && (
                <div style={{ marginBottom: "20px" }}>
                  <label
                    htmlFor="password"
                    style={{
                      display: "block",
                      marginBottom: "8px",
                      fontSize: "14px",
                      fontWeight: "500",
                      color: "#5f6368",
                    }}
                  >
                    Password
                  </label>
                  <input
                    type="password"
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter password"
                    style={{
                      width: "100%",
                      padding: "12px",
                      border: "1px solid #dadce0",
                      borderRadius: "8px",
                      fontSize: "14px",
                      outline: "none",
                      boxSizing: "border-box",
                    }}
                  />
                </div>
              )}

              <div style={{ display: "flex", gap: "12px" }}>
                <button
                  onClick={handleUpload}
                  disabled={
                    !selectedFile ||
                    (customLinkId.trim() !== "" &&
                      linkIdStatus.available === false)
                  }
                  style={{
                    flex: 1,
                    padding: "12px 24px",
                    background:
                      selectedFile &&
                      !(
                        customLinkId.trim() !== "" &&
                        linkIdStatus.available === false
                      )
                        ? "#4285f4"
                        : "#f1f3f4",
                    color:
                      selectedFile &&
                      !(
                        customLinkId.trim() !== "" &&
                        linkIdStatus.available === false
                      )
                        ? "white"
                        : "#9aa0a6",
                    border: "none",
                    borderRadius: "8px",
                    fontSize: "14px",
                    fontWeight: "500",
                    cursor:
                      selectedFile &&
                      !(
                        customLinkId.trim() !== "" &&
                        linkIdStatus.available === false
                      )
                        ? "pointer"
                        : "not-allowed",
                    transition: "all 0.2s ease",
                  }}
                >
                  Upload File
                </button>

                <button
                  onClick={() => setSelectedFile(null)}
                  style={{
                    padding: "12px 24px",
                    background: "#f8f9fa",
                    color: "#5f6368",
                    border: "1px solid #dadce0",
                    borderRadius: "8px",
                    fontSize: "14px",
                    fontWeight: "500",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                  }}
                >
                  Clear
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {isUploading && progress && (
        <div
          style={{
            background: "white",
            padding: "24px",
            borderRadius: "12px",
            border: "1px solid #e1e5e9",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
          }}
        >
          <h3
            style={{ margin: "0 0 16px 0", fontSize: "18px", color: "#202124" }}
          >
            Uploading: {progress.filename}
          </h3>

          <div
            style={{
              background: "#f1f3f4",
              borderRadius: "8px",
              height: "8px",
              overflow: "hidden",
              marginBottom: "12px",
            }}
          >
            <div
              style={{
                background: "linear-gradient(90deg, #4285f4, #34a853)",
                height: "100%",
                width: `${progress.progress}%`,
                transition: "width 0.3s ease",
                borderRadius: "8px",
              }}
            />
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              fontSize: "14px",
              color: "#5f6368",
              marginBottom: "8px",
            }}
          >
            <span>
              Part {progress.completedParts} of {progress.totalParts}
            </span>
            <span>{progress.progress}%</span>
          </div>

          <div
            style={{
              fontSize: "14px",
              color: "#9aa0a6",
              textAlign: "center",
            }}
          >
            {formatFileSize(progress.uploadedBytes)} of{" "}
            {formatFileSize(progress.totalSize)}
          </div>
        </div>
      )}
    </div>
  );
}

export default UploadPage;
